/**
 * Generateur d'alias e-mail (R19 / G23).
 *
 * Un alias par site : le site n'apprend jamais l'adresse reelle, et un alias
 * qui se met a recevoir du spam DESIGNE le site qui a fuite. Klavox ne devient
 * pas fournisseur de mail : il pilote le compte que l'utilisateur possede DEJA
 * chez un fournisseur, avec SA cle d'API -- MailKrypt par defaut ; addy.io et
 * SimpleLogin (tiers hors UE) seulement s'il les choisit.
 *
 * Zero-knowledge, ce que cela impose ici :
 *  - la cle d'API est un SECRET : elle voyage dans un EN-TETE, jamais dans
 *    l'URL (qui finit dans les journaux d'acces), jamais dans un message
 *    d'erreur, et elle n'est pas journalisee ;
 *  - l'appel part du NAVIGATEUR vers le fournisseur : le serveur Klavox n'est
 *    pas sur le chemin et ne voit ni la cle, ni l'alias, ni le site vise ;
 *  - une reponse inattendue (portail captif, page HTML) ne doit JAMAIS passer
 *    pour un alias : la reponse est validee, pas devinee.
 *
 * Voie souveraine SANTVIA, proposee PAR DEFAUT : MailKrypt, le serveur mail
 * de la galaxie, cree des alias jetables (R13, `POST /v1/aliases/bulk`) : partie locale tiree au
 * hasard COTE SERVEUR (64 bits), sur un domaine que la cle a le droit de
 * gerer, vers une adresse de destination. Ce n'est pas l'API d'addy.io : le
 * fournisseur `mailkrypt` porte donc deux champs de plus (domaine,
 * destination) et n'a pas de point d'acces par defaut -- aucune instance
 * SANTVIA n'est publiee, c'est celle de l'organisation qui s'y saisit.
 * Ce qu'il faut savoir avant de s'en servir, lu dans le code de MailKrypt :
 *  - la cle est un `mkk_...` d'administrateur DE DOMAINE (role
 *    `domain_admin`, cloisonne a ses domaines) ; une cle en lecture seule
 *    est refusee (403). Le libre-service d'un utilisateur final sans
 *    domaine a lui reste un arbitrage ouvert chez MailKrypt ;
 *  - l'API n'a PAS de CORS, volontairement : le point d'acces est la meme
 *    origine que celle qui sert `/v1` derriere le mandataire de la console
 *    (`services/console/nginx.conf`), pas le port de l'API ;
 *  - un alias expire GARDE sa place dans le plafond du domaine : seul le
 *    supprimer la libere (`deleteEmailAlias`).
 * addy.io et SimpleLogin auto-heberges restent possibles : on pointe
 * `endpoint` sur l'instance. C'est pourquoi le point d'acces est un champ,
 * pas une constante.
 */
import { normalizeHttpsEndpoint } from './https-endpoint.js';

export class EmailAliasError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EmailAliasError';
  }
}

export type AliasProviderId = 'addy' | 'simplelogin' | 'mailkrypt';

/** Ce qu'un fournisseur recoit pour creer un alias. Aucun secret ici : la cle part dans les en-tetes. */
export interface AliasRequest {
  readonly note: string;
  /** MailKrypt : domaine de l'alias (doit etre gere par la cle). */
  readonly domain: string;
  /** MailKrypt : adresse reelle vers laquelle l'alias fait suivre. */
  readonly destination: string;
}

export interface AliasProvider {
  readonly id: AliasProviderId;
  readonly label: string;
  /** Vide : pas de service public, le point d'acces doit etre saisi. */
  readonly defaultEndpoint: string;
  /** Chemin de creation, relatif au point d'acces. */
  readonly path: string;
  /** En-tetes portant la cle : AUCUN fournisseur ne la passe en parametre d'URL. */
  readonly authHeaders: (apiKey: string) => Record<string, string>;
  readonly body: (request: AliasRequest) => unknown;
  /** Extrait l'alias de la reponse JSON, ou `null` si la forme est inattendue. */
  readonly readAlias: (payload: unknown, request: AliasRequest) => string | null;
  /** Champs propres au fournisseur, controles AVANT tout appel reseau. */
  readonly requires?: readonly ('domain' | 'destination')[];
  /** Messages des refus propres au fournisseur (statut HTTP -> texte, sans rien recopier de la reponse). */
  readonly statusMessages?: Readonly<Record<number, string>>;
  /** Suppression d'un alias (chemin relatif au point d'acces), si le fournisseur la porte. */
  readonly deletePath?: (alias: string) => string;
}

function field(payload: unknown, ...path: readonly string[]): string | null {
  let node: unknown = payload;
  for (const key of path) {
    if (typeof node !== 'object' || node === null) return null;
    node = (node as Record<string, unknown>)[key];
  }
  return typeof node === 'string' ? node : null;
}

/**
 * MailKrypt EN TETE : c'est le fournisseur propose sans reglage. addy.io
 * (Royaume-Uni) et SimpleLogin (Suisse) restent au choix de l'utilisateur,
 * jamais un defaut : sans choix explicite, aucun appel ne part chez eux.
 */
export const ALIAS_PROVIDERS: readonly AliasProvider[] = [
  {
    id: 'mailkrypt',
    label: 'MailKrypt (serveur mail souverain SANTVIA)',
    defaultEndpoint: '',
    path: '/v1/aliases/bulk',
    authHeaders: (apiKey) => ({ Authorization: `Bearer ${apiKey}` }),
    // Un seul alias, partie locale tiree par le serveur. MailKrypt ne garde pas de note.
    body: ({ domain, destination }) => ({ domain, destination, count: 1 }),
    readAlias: (payload, { domain }) => {
      if (!Array.isArray(payload) || payload.length !== 1) return null;
      const source = field(payload[0], 'source');
      // Un alias hors du domaine demande n'est pas celui qu'on a cree.
      return source !== null && source.toLowerCase().endsWith(`@${domain}`) ? source : null;
    },
    requires: ['domain', 'destination'],
    statusMessages: {
      404: 'Domaine inconnu de MailKrypt ou hors de la portee de la cle',
      409: "Plafond d'alias du domaine atteint : supprimez des alias pour liberer des places",
      422: 'Domaine ou destination refuses par MailKrypt',
    },
    deletePath: (alias) => `/v1/aliases/${encodeURIComponent(alias)}`,
  },
  {
    id: 'addy',
    label: 'addy.io (tiers, hors UE -- option)',
    defaultEndpoint: 'https://app.addy.io',
    path: '/api/v1/aliases',
    authHeaders: (apiKey) => ({ Authorization: `Bearer ${apiKey}`, 'X-Requested-With': 'XMLHttpRequest' }),
    body: ({ note }) => ({ description: note, format: 'random_characters' }),
    readAlias: (payload) => field(payload, 'data', 'email'),
  },
  {
    id: 'simplelogin',
    label: 'SimpleLogin (tiers, hors UE -- option)',
    defaultEndpoint: 'https://app.simplelogin.io',
    path: '/api/alias/random/new',
    authHeaders: (apiKey) => ({ Authentication: apiKey }),
    body: ({ note }) => ({ note }),
    readAlias: (payload) => field(payload, 'alias'),
  },
];

/** Fournisseur retenu quand l'utilisateur n'en a choisi aucun : le souverain, pas un tiers. */
export const DEFAULT_ALIAS_PROVIDER_ID: AliasProviderId = 'mailkrypt';

export function aliasProvider(id: string): AliasProvider {
  const provider = ALIAS_PROVIDERS.find((candidate) => candidate.id === id);
  if (provider === undefined) throw new EmailAliasError("Fournisseur d'alias inconnu");
  return provider;
}

/** Forme minimale d'une adresse : pas une validation RFC, un garde-fou contre une reponse qui n'est pas un alias. */
const EMAIL = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[a-z]{2,}$/iu;

export function normalizeAliasEndpoint(raw: string): string {
  return normalizeHttpsEndpoint(raw, {
    label: "du fournisseur d'alias",
    fail: (message) => new EmailAliasError(message),
  });
}

const DOMAIN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/u;

function requestFor(provider: AliasProvider, note: string, domain: string, destination: string): AliasRequest {
  const request: AliasRequest = {
    note,
    domain: domain.trim().toLowerCase().replace(/^@/u, ''),
    destination: destination.trim(),
  };
  const requires = provider.requires ?? [];
  if (requires.includes('domain') && !DOMAIN.test(request.domain)) {
    throw new EmailAliasError("Domaine de l'alias manquant ou invalide");
  }
  if (requires.includes('destination') && !EMAIL.test(request.destination)) {
    throw new EmailAliasError("Adresse de destination de l'alias manquante ou invalide");
  }
  return request;
}

/**
 * Point d'acces controle, et JAMAIS le service public d'un AUTRE fournisseur :
 * la cle MailKrypt d'une organisation n'a rien a faire chez addy.io parce que
 * le champ avait garde la valeur du fournisseur precedent.
 */
function endpointFor(provider: AliasProvider, endpoint: string | undefined): string {
  const raw = endpoint ?? provider.defaultEndpoint;
  if (raw.trim() === '') throw new EmailAliasError(`Point d'acces ${provider.label.split(' ')[0]} manquant`);
  const base = normalizeAliasEndpoint(raw);
  const foreign = ALIAS_PROVIDERS.find(
    (other) => other.id !== provider.id && other.defaultEndpoint !== '' && normalizeAliasEndpoint(other.defaultEndpoint) === base,
  );
  if (foreign !== undefined) {
    throw new EmailAliasError(`Ce point d'acces est celui d'un autre fournisseur (${foreign.label.split(' ')[0]}) : la cle n'y sera pas envoyee`);
  }
  return base;
}

export interface CreateAliasOptions {
  /** Absent : `DEFAULT_ALIAS_PROVIDER_ID` (MailKrypt). */
  readonly providerId?: string;
  readonly apiKey: string;
  /** Point d'acces : defaut du fournisseur, ou instance auto-hebergee / souveraine. */
  readonly endpoint?: string;
  /** Note cote fournisseur (le site vise, en general). Aucun secret ici. */
  readonly note?: string;
  /** MailKrypt : domaine gere par la cle. */
  readonly domain?: string;
  /** MailKrypt : adresse reelle de destination. */
  readonly destination?: string;
  readonly fetchImpl?: typeof fetch;
}

/**
 * Cree un alias et renvoie l'adresse. Toute anomalie leve : un alias faux
 * serait pire qu'une erreur, l'utilisateur l'enregistrerait comme identifiant
 * d'un compte qu'il ne pourrait plus jamais recuperer.
 */
export async function createEmailAlias({
  providerId,
  apiKey,
  endpoint,
  note = '',
  domain = '',
  destination = '',
  fetchImpl = fetch,
}: CreateAliasOptions): Promise<string> {
  const provider = aliasProvider(providerId ?? DEFAULT_ALIAS_PROVIDER_ID);
  if (apiKey.trim() === '') throw new EmailAliasError("Cle d'API du fournisseur d'alias manquante");
  const request = requestFor(provider, note, domain, destination);
  const base = endpointFor(provider, endpoint);

  let response: Response;
  try {
    response = await fetchImpl(`${base}${provider.path}`, {
      method: 'POST',
      headers: {
        ...provider.authHeaders(apiKey),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(provider.body(request)),
      cache: 'no-store',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
    });
  } catch {
    // Volontairement muet sur la cause : un message de `fetch` peut recopier l'URL.
    throw new EmailAliasError("Fournisseur d'alias injoignable");
  }
  if (response.status === 401 || response.status === 403) {
    throw new EmailAliasError("Cle d'API refusee par le fournisseur d'alias");
  }
  const known = provider.statusMessages?.[response.status];
  if (known !== undefined) throw new EmailAliasError(known);
  if (!response.ok) {
    throw new EmailAliasError(`Fournisseur d'alias indisponible (HTTP ${response.status})`);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new EmailAliasError("Reponse du fournisseur d'alias illisible");
  }
  const alias = provider.readAlias(payload, request)?.trim() ?? '';
  if (!EMAIL.test(alias)) throw new EmailAliasError("Le fournisseur n'a pas renvoye d'alias exploitable");
  return alias;
}

export interface DeleteAliasOptions {
  readonly providerId: string;
  readonly apiKey: string;
  readonly endpoint?: string;
  readonly alias: string;
  readonly fetchImpl?: typeof fetch;
}

/** Vrai si le fournisseur sait supprimer un alias depuis Klavox. */
export function canDeleteAlias(providerId: string): boolean {
  return ALIAS_PROVIDERS.find((candidate) => candidate.id === providerId)?.deletePath !== undefined;
}

/**
 * Supprime un alias. Chez MailKrypt c'est la SEULE facon de rendre sa place
 * dans le plafond du domaine : un alias expire ou desactive la garde. Un 404
 * n'est PAS un succes : MailKrypt repond 404 aussi quand la cle ne voit pas le
 * domaine, et annoncer « supprime » laisserait l'alias recevoir le courrier.
 */
export async function deleteEmailAlias({
  providerId,
  apiKey,
  endpoint,
  alias,
  fetchImpl = fetch,
}: DeleteAliasOptions): Promise<void> {
  const provider = aliasProvider(providerId);
  if (provider.deletePath === undefined) throw new EmailAliasError('Ce fournisseur ne permet pas de supprimer un alias depuis Klavox');
  if (apiKey.trim() === '') throw new EmailAliasError("Cle d'API du fournisseur d'alias manquante");
  const address = alias.trim();
  if (!EMAIL.test(address)) throw new EmailAliasError('Alias a supprimer invalide');
  const base = endpointFor(provider, endpoint);

  let response: Response;
  try {
    response = await fetchImpl(`${base}${provider.deletePath(address)}`, {
      method: 'DELETE',
      headers: { ...provider.authHeaders(apiKey), Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
    });
  } catch {
    throw new EmailAliasError("Fournisseur d'alias injoignable");
  }
  if (response.status === 401 || response.status === 403) {
    throw new EmailAliasError("Cle d'API refusee par le fournisseur d'alias");
  }
  if (response.status === 404) throw new EmailAliasError('Alias inconnu de MailKrypt ou hors de la portee de la cle');
  if (!response.ok) throw new EmailAliasError(`Fournisseur d'alias indisponible (HTTP ${response.status})`);
}
